import React, { useEffect, useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FiArrowDown, FiArrowUp, FiClock, FiDollarSign, FiMinus, FiRefreshCw } from "react-icons/fi";
import { MONEY_METRICS, PAYMENT_STATUS_TONE, PAYMENT_TYPE_OPTIONS, REFUND_STATUS_TONE } from "../adminConstants";
import {
  formatBookingSlot,
  formatCurrency,
  formatCurrencyCompact,
  formatShortId,
  formatTimestamp,
} from "../adminUtils";

const AdminPaymentsTab = ({
  t,
  isActive,
  moneyLoading,
  moneyError,
  moneySummaryCards,
  moneyMonth,
  setMoneyMonth,
  moneyMonthOptions,
  moneyYear,
  setMoneyYear,
  moneyYearOptions,
  moneyMetric,
  setMoneyMetric,
  moneyMetricLabel,
  moneyRangeLabel,
  moneyMetricTotal,
  moneyCurrency,
  moneyHasData,
  moneyChartData,
  moneyMetricKey,
  moneyChartStyle,
  handleExportMonthlyRevenue,
  handleExportVatReady,
  handleExportAccountantCsv,
  paymentSearchQuery,
  setPaymentSearchQuery,
  handlePaymentSearch,
  paymentStatusFilter,
  setPaymentStatusFilter,
  paymentTypeFilter,
  setPaymentTypeFilter,
  paymentStatusOptions,
  paymentSortTimeDir,
  setPaymentSortTimeDir,
  paymentSortAmountDir,
  setPaymentSortAmountDir,
  handleResetPaymentFilters,
  paymentsLoading,
  paymentsError,
  payments,
  paymentAppliedQuery,
  expandedPaymentRowId,
  handlePaymentRowToggle,
  paymentsPage,
  setPaymentsPage,
  paymentsPageSize,
  toggleSortDirRequired,
  toggleSortDir,
}) => {
  const isYearView = moneyMonth === -1;
  const paymentRows = useMemo(() => {
    if (!Array.isArray(payments)) return [];
    const rows = [];
    payments.forEach((payment) => {
      const paidAmount = Number.isFinite(payment.paidAmount)
        ? payment.paidAmount
        : payment.amount;
      rows.push({
        rowId: `payment-${payment.id}`,
        kind: "payment",
        status: payment.status,
        amount: paidAmount,
        currency: payment.currency || "EUR",
        createdUtc: payment.createdUtc,
        paymentId: payment.id,
        bookingId: payment.bookingId,
        contactEmail: payment.contactEmail,
        contactPhone: payment.contactPhone,
        locationName: payment.locationName,
        locationId: payment.locationId,
        provider: payment.provider,
        providerReference: payment.providerReference,
        bookingStatus: payment.bookingStatus,
        bookingType: payment.bookingType,
        startUtc: payment.startUtc,
        endUtc: payment.endUtc,
        refunds: Array.isArray(payment.refunds) ? payment.refunds : [],
      });
      (payment.refunds || []).forEach((refund) => {
        rows.push({
          rowId: `refund-${refund.id}`,
          kind: "refund",
          status: refund.status,
          amount: refund.amount,
          currency: payment.currency || "EUR",
          createdUtc: refund.createdUtc,
          paymentId: payment.id,
          bookingId: payment.bookingId,
          contactEmail: payment.contactEmail,
          contactPhone: payment.contactPhone,
          locationName: payment.locationName,
          locationId: payment.locationId,
          provider: payment.provider,
          providerReference: payment.providerReference,
          bookingStatus: payment.bookingStatus,
          bookingType: payment.bookingType,
          startUtc: payment.startUtc,
          endUtc: payment.endUtc,
          refundReason: refund.reason,
          refundNotes: refund.notes,
          createdByEmail: refund.createdByEmail,
          processedUtc: refund.processedUtc,
          refund,
        });
      });
    });
    return rows.sort((a, b) => new Date(b.createdUtc) - new Date(a.createdUtc));
  }, [payments]);

  const filteredPaymentRows = useMemo(() => {
    let rows = [...paymentRows];
    if (paymentAppliedQuery) {
      const query = paymentAppliedQuery.toLowerCase();
      rows = rows.filter((row) => {
        const values = [
          row.bookingId,
          formatShortId(row.bookingId),
          row.paymentId,
          formatShortId(row.paymentId),
          row.providerReference,
          row.contactEmail,
          row.contactPhone,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());
        return values.some((value) => value.includes(query));
      });
    }
    if (paymentStatusFilter !== "all") {
      rows = rows.filter((row) => row.status === paymentStatusFilter);
    }
    if (paymentTypeFilter !== "all") {
      rows = rows.filter((row) => row.kind === paymentTypeFilter);
    }
    rows.sort((a, b) => {
      if (paymentSortAmountDir !== "none") {
        const diff = (a.amount ?? 0) - (b.amount ?? 0);
        if (diff !== 0) {
          return paymentSortAmountDir === "asc" ? diff : -diff;
        }
      }
      if (paymentSortTimeDir !== "none") {
        const aTime = new Date(a.createdUtc).getTime();
        const bTime = new Date(b.createdUtc).getTime();
        if (aTime !== bTime) {
          return paymentSortTimeDir === "asc" ? aTime - bTime : bTime - aTime;
        }
      }
      return 0;
    });

    return rows;
  }, [
    paymentAppliedQuery,
    paymentRows,
    paymentSortAmountDir,
    paymentSortTimeDir,
    paymentStatusFilter,
    paymentTypeFilter,
  ]);

  const paymentsPageCount = useMemo(() => {
    if (!filteredPaymentRows.length) return 1;
    return Math.max(1, Math.ceil(filteredPaymentRows.length / paymentsPageSize));
  }, [filteredPaymentRows.length, paymentsPageSize]);

  const pagedPaymentRows = useMemo(() => {
    const start = (paymentsPage - 1) * paymentsPageSize;
    return filteredPaymentRows.slice(start, start + paymentsPageSize);
  }, [filteredPaymentRows, paymentsPage, paymentsPageSize]);

  useEffect(() => {
    setPaymentsPage((current) => Math.min(Math.max(current, 1), paymentsPageCount));
  }, [paymentsPageCount, setPaymentsPage]);

  return (
    <div
      className={`admin-tab ${isActive ? "admin-tab--active" : ""
        }`.trim()}
    >
      <section className="admin-section admin-money admin-animate admin-animate--delay-1">
        <header>
          <div>
            <p className="admin-section__eyebrow">
              {t("admin.money.eyebrow", "Money")}
            </p>
            <h2>{t("admin.money.tabHeading", "Money overview")}</h2>
          </div>
        </header>
        {moneyLoading ? (
          <p className="admin-empty">
            {t("admin.money.loading", "Loading money overview...")}
          </p>
        ) : moneyError ? (
          <p className="admin-empty">{moneyError}</p>
        ) : (
          <>
            <div className="admin-overview__cards admin-money__summary">
              {moneySummaryCards.map((card) => (
                <article
                  key={card.id}
                  className={`admin-metric admin-metric--${card.tone}`.trim()}
                >
                  <p className="admin-metric__label">{card.label}</p>
                  <p className="admin-metric__value">{card.value}</p>
                  <p className="admin-metric__detail">{card.detail}</p>
                </article>
              ))}
            </div>
            <div className="admin-money__controls">
              <div className="admin-money__filters">
                <div className="admin-form__row">
                  <label className="admin-form__label" htmlFor="moneyMonth">
                    {t("admin.money.month", "Month")}
                  </label>
                  <select
                    id="moneyMonth"
                    className="admin-select"
                    value={moneyMonth}
                    onChange={(event) => setMoneyMonth(Number(event.target.value))}
                  >
                    {moneyMonthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form__row">
                  <label className="admin-form__label" htmlFor="moneyYear">
                    {t("admin.money.year", "Year")}
                  </label>
                  <select
                    id="moneyYear"
                    className="admin-select"
                    value={moneyYear}
                    onChange={(event) => setMoneyYear(Number(event.target.value))}
                  >
                    {moneyYearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="admin-money__metrics" role="tablist">
                {MONEY_METRICS.map((metric) => (
                  <button
                    key={metric.id}
                    type="button"
                    role="tab"
                    aria-selected={moneyMetric === metric.id}
                    className={`admin-toggle ${moneyMetric === metric.id ? "admin-toggle--active" : ""
                      }`.trim()}
                    onClick={() => setMoneyMetric(metric.id)}
                  >
                    {t(metric.labelKey, metric.labelFallback)}
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-chart">
              <div className="admin-chart__header">
                <div>
                  <h3>{t("admin.money.chartHeading", "Monthly trend")}</h3>
                  <p>
                    {t(
                      "admin.money.chartSubheading",
                      `Showing ${moneyMetricLabel} for ${moneyRangeLabel}.`
                    )}
                  </p>
                </div>
                <div className="admin-chart__total">
                  <span className="admin-chart__total-label">
                    {t("admin.money.total", "Total")}
                  </span>
                  <span className="admin-chart__total-value">
                    {formatCurrency(moneyMetricTotal, moneyCurrency)}
                  </span>
                </div>
              </div>
              {moneyHasData ? (
                <div
                  className="admin-chart__canvas"
                  role="img"
                  aria-label={t(
                    "admin.money.chartAria",
                    "Daily booking totals"
                  )}
                >
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart
                      data={moneyChartData}
                      margin={{ top: 10, right: 24, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient
                          id="moneyRevenue"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#208E76" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#208E76" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient
                          id="moneyProfit"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#176D7A" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#176D7A" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient
                          id="moneyRefunds"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop offset="5%" stopColor="#CE6F3F" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#CE6F3F" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="4 6" stroke="rgba(15, 45, 46, 0.12)" />
                      <XAxis
                        dataKey={isYearView ? "label" : "day"}
                        tickLine={false}
                        axisLine={false}
                        interval={isYearView ? 0 : "preserveStartEnd"}
                        tick={{ fontSize: 12, fill: "#6B7C7D" }}
                      />
                      <YAxis
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12, fill: "#6B7C7D" }}
                        tickFormatter={(value) => formatCurrencyCompact(value, moneyCurrency)}
                      />
                      <Tooltip
                        isAnimationActive={false}
                        cursor={{ stroke: "rgba(15, 45, 46, 0.2)", strokeWidth: 1 }}
                        wrapperStyle={{ pointerEvents: "none" }}
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const point = payload[0];
                          const label = point?.payload?.label;
                          return (
                            <div className="admin-chart__tooltip">
                              <p className="admin-chart__tooltip-title">{label}</p>
                              <p className="admin-chart__tooltip-value">
                                {moneyMetricLabel}:{" "}
                                {formatCurrency(point.value, moneyCurrency)}
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey={moneyMetricKey}
                        stroke={moneyChartStyle.stroke}
                        strokeWidth={2.5}
                        fill={`url(#${moneyChartStyle.gradientId})`}
                        dot={false}
                        activeDot={{ r: 5 }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="admin-empty">
                  {t(
                    "admin.money.empty",
                    "No booking activity for this period."
                  )}
                </p>
              )}
            </div>
            <div className="admin-money__exports">
              <div className="admin-money__exports-header">
                <div>
                  <h3>{t("admin.money.exportsHeading", "Exports")}</h3>
                  <p>
                    {t(
                      "admin.money.exportsSubheading",
                      "Download revenue, VAT-ready, and accountant files."
                    )}
                  </p>
                </div>
              </div>
              <p className="admin-money__exports-note">
                {t(
                  "admin.money.exportsNote",
                  "Exports use stored VAT snapshots per payment."
                )}
              </p>
              <div className="admin-money__exports-actions">
                <button
                  type="button"
                  className="admin-button admin-button--outline"
                  onClick={handleExportMonthlyRevenue}
                >
                  {t("admin.money.exportMonthly", "Monthly revenue")}
                </button>
                <button
                  type="button"
                  className="admin-button admin-button--outline"
                  onClick={handleExportVatReady}
                >
                  {t("admin.money.exportVat", "VAT-ready export")}
                </button>
                <button
                  type="button"
                  className="admin-button admin-button--primary"
                  onClick={handleExportAccountantCsv}
                >
                  {t("admin.money.exportAccountant", "Accountant-friendly CSV")}
                </button>
              </div>
            </div>
            <div className="admin-money__payments-header">
              <div>
                <h3>{t("admin.money.paymentsHeading", "Payments & refunds")}</h3>
                <p>
                  {t(
                    "admin.money.paymentsSubheading",
                    `All transactions for ${moneyRangeLabel}.`
                  )}
                </p>
              </div>
            </div>
            <div className="admin-search">
              <input
                type="search"
                className="admin-input"
                value={paymentSearchQuery}
                onChange={(event) => setPaymentSearchQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handlePaymentSearch();
                  }
                }}
                placeholder={t(
                  "admin.money.searchPlaceholder",
                  "Search by payment ID, booking ID, email, or phone"
                )}
                aria-label={t("admin.money.searchLabel", "Search payments")}
              />
              <button
                type="button"
                className="admin-button admin-button--primary"
                onClick={handlePaymentSearch}
              >
                {t("admin.actions.search", "Search")}
              </button>
            </div>
            <div className="admin-filters">
              <div className="admin-filters__group">
                <label className="admin-form__label" htmlFor="paymentStatusFilter">
                  {t("admin.money.statusFilter", "Status")}
                </label>
                <select
                  id="paymentStatusFilter"
                  className="admin-select admin-select--compact"
                  value={paymentStatusFilter}
                  onChange={(event) => setPaymentStatusFilter(event.target.value)}
                >
                  {paymentStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status === "all"
                        ? t("admin.money.allStatuses", "All statuses")
                        : status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-filters__group">
                <label className="admin-form__label" htmlFor="paymentTypeFilter">
                  {t("admin.money.typeFilter", "Type")}
                </label>
                <select
                  id="paymentTypeFilter"
                  className="admin-select admin-select--compact"
                  value={paymentTypeFilter}
                  onChange={(event) => setPaymentTypeFilter(event.target.value)}
                >
                  {PAYMENT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin-filters__group admin-filters__group--compact">
                <button
                  type="button"
                  id="paymentSortTime"
                  className={`admin-icon-toggle admin-icon-toggle--time ${paymentSortTimeDir !== "none" ? "admin-icon-toggle--active" : ""
                    }`.trim()}
                  onClick={() =>
                    setPaymentSortTimeDir((current) => toggleSortDirRequired(current))
                  }
                  aria-label={t("admin.money.sortTime", "Sort by time")}
                  title={t("admin.money.sortTime", "Sort by time")}
                >
                  <FiClock aria-hidden="true" />
                  {paymentSortTimeDir === "asc" ? (
                    <FiArrowUp aria-hidden="true" />
                  ) : (
                    <FiArrowDown aria-hidden="true" />
                  )}
                </button>
              </div>
              <div className="admin-filters__group admin-filters__group--compact">
                <button
                  type="button"
                  id="paymentSortAmount"
                  className={`admin-icon-toggle admin-icon-toggle--price ${paymentSortAmountDir !== "none" ? "admin-icon-toggle--active" : ""
                    }`.trim()}
                  onClick={() => setPaymentSortAmountDir((current) => toggleSortDir(current))}
                  aria-label={t("admin.money.sortAmount", "Sort by amount")}
                  title={t("admin.money.sortAmount", "Sort by amount")}
                >
                  <FiDollarSign aria-hidden="true" />
                  {paymentSortAmountDir === "asc" ? (
                    <FiArrowUp aria-hidden="true" />
                  ) : paymentSortAmountDir === "desc" ? (
                    <FiArrowDown aria-hidden="true" />
                  ) : (
                    <FiMinus aria-hidden="true" />
                  )}
                </button>
              </div>
              <div className="admin-filters__actions">
                <button
                  type="button"
                  className="admin-icon-button"
                  onClick={handleResetPaymentFilters}
                  aria-label={t("admin.money.resetFilters", "Reset filters")}
                  title={t("admin.money.resetFilters", "Reset filters")}
                >
                  <FiRefreshCw aria-hidden="true" focusable="false" />
                </button>
              </div>
            </div>
            <div className="admin-money__payments">
              {paymentsLoading ? (
                <p className="admin-empty">
                  {t("admin.money.paymentsLoading", "Loading payments...")}
                </p>
              ) : paymentsError ? (
                <p className="admin-empty">{paymentsError}</p>
              ) : filteredPaymentRows.length === 0 ? (
                <p className="admin-empty">
                  {t(
                    "admin.money.paymentsEmpty",
                    "No payments or refunds for this period."
                  )}
                </p>
              ) : (
                <>
                  <div className="admin-payments">
                    {pagedPaymentRows.map((row) => {
                      const statusTone =
                        row.kind === "payment"
                          ? PAYMENT_STATUS_TONE[row.status] || "neutral"
                          : REFUND_STATUS_TONE[row.status] || "neutral";
                      const amountLabel = formatCurrency(row.amount, row.currency);
                      const createdLabel = formatTimestamp(row.createdUtc);
                      return (
                        <article
                          key={row.rowId}
                          className={`admin-payment ${expandedPaymentRowId === row.rowId
                              ? "admin-payment--active"
                              : ""
                            }`.trim()}
                        >
                          <div className="admin-payment__main">
                            <div>
                              <p className="admin-payment__label">
                                {row.kind === "payment"
                                  ? t("admin.money.payment", "Payment")
                                  : t("admin.money.refund", "Refund")}
                              </p>
                              <h3 className="admin-payment__value">{amountLabel}</h3>
                              <p className="admin-payment__meta">
                                {row.contactEmail || t("admin.bookings.detail.none", "—")}
                              </p>
                              <p className="admin-payment__sub">
                                {t("admin.money.paymentId", "Payment ID")} ·{" "}
                                {formatShortId(row.paymentId)}
                              </p>
                            </div>
                            <div className="admin-payment__badges">
                              <span
                                className={`admin-pill admin-pill--${statusTone}`.trim()}
                              >
                                {row.status}
                              </span>
                              {row.kind === "refund" ? (
                                <span className="admin-pill admin-pill--warning">
                                  {t("admin.money.refundBadge", "Refund")}
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="admin-payment__footer">
                            <span className="admin-item__note">
                              {createdLabel} · {row.locationName}
                            </span>
                            <button
                              type="button"
                              className="admin-button admin-button--ghost"
                              onClick={() => handlePaymentRowToggle(row.rowId)}
                            >
                              {expandedPaymentRowId === row.rowId
                                ? t("admin.actions.hideDetails", "Hide details")
                                : t("admin.actions.viewDetails", "View details")}
                            </button>
                          </div>
                          {expandedPaymentRowId === row.rowId && (
                            <div className="admin-booking__details">
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.bookingId", "Booking ID")}
                                </p>
                                <p className="admin-detail-value">
                                  {row.bookingId}
                                </p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.bookingType", "Booking type")}
                                </p>
                                <p className="admin-detail-value">
                                  {row.bookingType}
                                </p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.bookingStatus", "Booking status")}
                                </p>
                                <p className="admin-detail-value">
                                  {row.bookingStatus}
                                </p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.time", "Booking time")}
                                </p>
                                <p className="admin-detail-value">
                                  {formatBookingSlot(row.startUtc, row.endUtc)}
                                </p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.phone", "Phone")}
                                </p>
                                <p className="admin-detail-value">
                                  {row.contactPhone || t("admin.bookings.detail.none", "—")}
                                </p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t("admin.money.detail.provider", "Provider")}
                                </p>
                                <p className="admin-detail-value">{row.provider}</p>
                              </div>
                              <div>
                                <p className="admin-detail-label">
                                  {t(
                                    "admin.money.detail.providerRef",
                                    "Provider reference"
                                  )}
                                </p>
                                <p className="admin-detail-value">
                                  {row.providerReference}
                                </p>
                              </div>
                              <div>
                              </div>
                              {row.kind === "payment" ? (
                                <>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.paid", "Paid amount")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {formatCurrency(row.amount, row.currency)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.refunded", "Refunded total")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {formatCurrency(
                                        row.refunds.reduce(
                                          (sum, refund) => sum + (refund.amount || 0),
                                          0
                                        ),
                                        row.currency
                                      )}
                                    </p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.reason", "Reason")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {row.refundReason || "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.notes", "Notes")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {row.refundNotes || "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.createdBy", "Created by")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {row.createdByEmail || "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="admin-detail-label">
                                      {t("admin.money.detail.processed", "Processed")}
                                    </p>
                                    <p className="admin-detail-value">
                                      {row.processedUtc
                                        ? formatTimestamp(row.processedUtc)
                                        : "—"}
                                    </p>
                                  </div>
                                </>
                              )}
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                  <div className="admin-money__pagination">
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() =>
                        setPaymentsPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={paymentsPage <= 1}
                      aria-label={t("admin.actions.prev", "Prev")}
                    >
                      {"<"}
                    </button>
                    <span className="admin-money__page">
                      {t("admin.pagination.page", "Page")} {paymentsPage}{" "}
                      {t("admin.pagination.of", "of")} {paymentsPageCount}
                    </span>
                    <button
                      type="button"
                      className="admin-button admin-button--ghost"
                      onClick={() =>
                        setPaymentsPage((prev) =>
                          Math.min(paymentsPageCount, prev + 1)
                        )
                      }
                      disabled={paymentsPage >= paymentsPageCount}
                      aria-label={t("admin.actions.next", "Next")}
                    >
                      {">"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </section>
    </div>

  );
};

export default AdminPaymentsTab;
